import { useState } from "react";
import type { ReactNode } from "react";
import type { UiManagerMainRoute, UiSnapshotV2 } from "@/ui/runtime/contracts/manager-ui-v2";
import {
    ShellPane,
    PaneContent,
    PaneFooter,
    PaneGroup,
    PaneGroupContent,
    PaneHeader,
    ShellMain,
    PaneMenu,
    PaneMenuButton,
    PaneMenuItem,
    PaneInput,
    useShell,
} from "@/ui/foundation/primitives/shell";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui/foundation/primitives/card";
import { cn } from "@/ui/foundation/lib/utils";
import { DataList } from "@/ui/components/common/data-list";
import { helpArticles, RAIL_ICON_CLASS, RAIL_ICON_STROKE } from "@/ui/components/manager/manager-ui-helpers";
import {
    CalendarDaysIcon,
    CircleHelpIcon,
    HomeIcon,
    InboxIcon,
    MoonIcon,
    SearchIcon,
    ShieldIcon,
    SunIcon,
    TargetIcon,
    TrophyIcon,
    UsersIcon,
} from "lucide-react";

const navIconById = {
    home: HomeIcon,
    competitions: TrophyIcon,
    inbox: InboxIcon,
    squad: UsersIcon,
    fixtures: CalendarDaysIcon,
    staff: ShieldIcon,
    transfers: TargetIcon,
} as const;

interface ManagerLayoutProps {
    snapshot: UiSnapshotV2;
    darkMode: boolean;
    onToggleTheme(): void;
    onNavigate(route: UiManagerMainRoute): void;
    secondaryVisible: boolean;
    secondaryPane: ReactNode;
    mainContent: ReactNode;
    overlayContent: ReactNode;
}

interface ManagerSecondaryPaneProps {
    title: string;
    headerAction?: ReactNode;
    search?: {
        value: string;
        onChange(value: string): void;
        placeholder?: string;
    };
    children: ReactNode;
}

function HelpSecondaryPane({ selectedArticleId, onSelectArticle }: { selectedArticleId: string; onSelectArticle(id: string): void }) {
    return (
        <ManagerSecondaryPane title="Help">
            <DataList
                items={helpArticles}
                getKey={(article) => article.id}
                selectedKey={selectedArticleId}
                onSelect={(article) => onSelectArticle(article.id)}
                itemClassName="flex flex-col items-start gap-1"
                renderItem={(article) => (
                    <>
                        <span className="text-sm font-medium">{article.title}</span>
                        <span className="text-xs text-muted-foreground">{article.excerpt}</span>
                    </>
                )}
            />
        </ManagerSecondaryPane>
    );
}

function HelpMainContent({ selectedArticleId }: { selectedArticleId: string }) {
    const article = helpArticles.find((item) => item.id === selectedArticleId) ?? helpArticles[0];
    return (
        <Card>
            <CardHeader>
                <CardDescription>Help Article</CardDescription>
                <CardTitle>{article.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>{article.body}</p>
                <div className="rounded-md border bg-muted/30 p-3">
                    Tip: this section is a prototype and will be expanded with game-specific guides.
                </div>
            </CardContent>
        </Card>
    );
}

function ManagerNavRail({
    snapshot,
    darkMode,
    showHelp,
    onToggleHelp,
    onToggleTheme,
    onNavigate,
}: {
    snapshot: UiSnapshotV2;
    darkMode: boolean;
    showHelp: boolean;
    onToggleHelp(): void;
    onToggleTheme(): void;
    onNavigate(route: UiManagerMainRoute): void;
}) {
    const { setOpen } = useShell();

    return (
        <ShellPane collapsible="none" className="h-full min-h-0 w-[calc(var(--manager-nav-rail-width)+1px)] border-r">
            <PaneContent>
                <PaneGroup>
                    <PaneGroupContent className="px-1.5 md:px-0">
                        <PaneMenu className="flex flex-col gap-1.5">
                            {snapshot.navItems.map((item) => {
                                const Icon = navIconById[item.id];
                                return (
                                    <PaneMenuItem key={item.id}>
                                        <PaneMenuButton
                                            isActive={snapshot.activeRoute === item.route}
                                            className={cn(
                                                "h-auto flex-col items-center justify-center gap-1 px-1 py-2.5 md:px-1 data-[active=true]:bg-primary! data-[active=true]:text-primary-foreground! data-[active=true]:ring-1 data-[active=true]:ring-primary/35 data-[active=true]:shadow-sm data-[active=true]:[&>svg]:text-primary-foreground!",
                                                snapshot.activeRoute === item.route
                                                    ? "bg-primary text-primary-foreground"
                                                    : "text-pane-foreground/65 hover:bg-(--manager-list-item-hover-bg) hover:text-foreground hover:[&>svg]:text-foreground"
                                            )}
                                            onClick={() => {
                                                onNavigate(item.route);
                                                setOpen(true);
                                            }}
                                        >
                                            <span className="relative">
                                                <Icon className={RAIL_ICON_CLASS} strokeWidth={RAIL_ICON_STROKE} />
                                                {item.id === "inbox" && (item.badgeCount ?? 0) > 0 ? (
                                                    <span className="absolute -right-2 -top-1 inline-flex min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] leading-4 font-bold text-white">
                                                        {Math.min(99, item.badgeCount ?? 0)}
                                                    </span>
                                                ) : null}
                                            </span>
                                            <span className="text-[8px] leading-none font-bold uppercase tracking-[0.08em]">{item.label}</span>
                                        </PaneMenuButton>
                                    </PaneMenuItem>
                                );
                            })}
                        </PaneMenu>
                    </PaneGroupContent>
                </PaneGroup>
            </PaneContent>
            <PaneFooter>
                <div className="flex flex-col gap-2 p-1">
                    <PaneMenuButton
                        className="h-auto flex-col items-center justify-center gap-1 px-1 py-2 md:px-1"
                        onClick={onToggleTheme}
                    >
                        {darkMode ? <SunIcon className={RAIL_ICON_CLASS} strokeWidth={RAIL_ICON_STROKE} /> : <MoonIcon className={RAIL_ICON_CLASS} strokeWidth={RAIL_ICON_STROKE} />}
                        <span className="sr-only">Toggle theme</span>
                    </PaneMenuButton>
                    <PaneMenuButton
                        className={cn("h-auto flex-col items-center justify-center gap-1 px-1 py-2 md:px-1", showHelp ? "bg-primary text-primary-foreground" : "")}
                        onClick={onToggleHelp}
                    >
                        <CircleHelpIcon className={RAIL_ICON_CLASS} strokeWidth={RAIL_ICON_STROKE} />
                        <span className="sr-only">Help</span>
                    </PaneMenuButton>
                </div>
            </PaneFooter>
        </ShellPane>
    );
}

export function ManagerSecondaryPane({ title, headerAction, search, children }: ManagerSecondaryPaneProps) {
    return (
        <>
            <PaneHeader className="gap-3.5 border-b p-4">
                <div className="flex items-center justify-between gap-2">
                    <div className="text-base font-medium text-foreground">{title}</div>
                    {headerAction ?? null}
                </div>
                {search ? (
                    <div className="relative">
                        <SearchIcon className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                        <PaneInput
                            value={search.value}
                            onChange={(event) => search.onChange(event.currentTarget.value)}
                            placeholder={search.placeholder ?? "Type to search..."}
                            className="pl-8"
                        />
                    </div>
                ) : null}
            </PaneHeader>
            <PaneContent>{children}</PaneContent>
        </>
    );
}

export function ManagerLayout({
    snapshot,
    darkMode,
    onToggleTheme,
    onNavigate,
    secondaryVisible,
    secondaryPane,
    mainContent,
    overlayContent,
}: ManagerLayoutProps) {
    const [showHelp, setShowHelp] = useState<boolean>(false);
    const [selectedHelpArticleId, setSelectedHelpArticleId] = useState<string>(helpArticles[0].id);

    const showSecondaryPane = showHelp || secondaryVisible;

    return (
        <>
            <ManagerNavRail
                snapshot={snapshot}
                darkMode={darkMode}
                showHelp={showHelp}
                onToggleHelp={() => setShowHelp((value) => !value)}
                onToggleTheme={onToggleTheme}
                onNavigate={onNavigate}
            />
            {showSecondaryPane ? (
                <ShellPane collapsible="none" className="hidden h-full min-h-0 w-(--manager-secondary-pane-width) border-r md:flex">
                    {showHelp ? (
                        <HelpSecondaryPane
                            selectedArticleId={selectedHelpArticleId}
                            onSelectArticle={setSelectedHelpArticleId}
                        />
                    ) : (
                        secondaryPane
                    )}
                </ShellPane>
            ) : null}
            <ShellMain className="h-full min-h-0 min-w-0 overflow-y-auto bg-(--manager-main-surface)">
                <div className="min-h-full space-y-4 p-4 pb-8">
                    {overlayContent}
                    {snapshot.activeOverlayRoute !== null ? null : (showHelp ? <HelpMainContent selectedArticleId={selectedHelpArticleId} /> : mainContent)}
                </div>
            </ShellMain>
        </>
    );
}
